/// <amd-module name="Widgets/CumulativeFlowDiagram/CumulativeFlowDiagramSettingsManager" />
import { LowestLevelVisibleBoardQuery } from "Analytics/Boards/CumulativeFlowDiagramQueries/LowestLevelVisibleBoardQuery";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { getProject } from "Tfs/Widget/Context";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Resources from "Widgets/CumulativeFlowDiagram/Resources";
import { CumulativeFlowDiagramSettings } from "./CumulativeFlowDiagramContracts";
import { daysBetweenDates } from "./DateUtils";
import { PlaceholderBoardUnsupportedError } from "./PlaceholderBoardUnsupportedError";
import { widgetCommand } from "./QueryUtilities";

export type ConfigProps = { name: string } & ({ settings: string; teamContext?: any } | { settings: null; teamContext: WidgetTeamContext });

/**
 * Responsible for parsing widget settings strings, providing defaults if no settings are given,
 * and ensuring certain values of the settings are within allowed ranges.
 */
export class CumulativeFlowDiagramSettingsManager {
    /**
     * While the Analytics service is likely capable of handling a larger number, this value was chosen to have parity with the
     * existing agile experiences CFD (max of 6 months) and to limit sending large values that would not only take more time to retrieve
     * a result but would not be presented well because of the data density.
     */
    private static readonly maxAllowedTimePeriodDays: number = 180;

    /**
     * Minimum allowed time period that user can enter.
     * Lower values such as 3 days look odd on the chart, so a minimum was picked based upon what would look somewhat reasonable
     * and be useful on the chart as well as based upon what most people were saving for rolling periods according to telemetry.
     * 30 days and above was the common rolling period chosen.
     */
    private static readonly minAllowedTimePeriodDays: number = 14;

    public constructor(protected context: IVssPageContext, protected command: string = widgetCommand) {}

    /**
     * Parses the custom settings of the given widget config.
     * Or if there are no custom settings, generates parsed default settings to return.
     * @param configProps contains the name of the widget and the widget settings to parse or team context.
     * @param endDate is used to ensure StartDate time period settings remain within allowed ranges
     */
    public async ensureAndParseSettings(
        configProps: ConfigProps,
        endDate: string
    ): Promise<{ title: string; settings: CumulativeFlowDiagramSettings }> {
        return configProps.settings != null
            ? {
                  title: configProps.name,
                  settings: this.parseSettings(configProps.settings, endDate)
              }
            : this.createDefaultSettings(configProps.teamContext);
    }

    private parseSettings(settings: string, endDateString: string): CumulativeFlowDiagramSettings {
        const parsedSettings: CumulativeFlowDiagramSettings = JSON.parse(settings);

        // Clamp the time period to the allowed range
        const { timePeriod } = parsedSettings.chartDataSettings;
        if (timePeriod.identifier === "RollingPeriod") {
            timePeriod.settings = Math.max(
                CumulativeFlowDiagramSettingsManager.minAllowedTimePeriodDays,
                Math.min(CumulativeFlowDiagramSettingsManager.maxAllowedTimePeriodDays, timePeriod.settings)
            );
        } else {
            const daysSinceStartDate = daysBetweenDates(timePeriod.settings, endDateString);
            const endDate = new Date(endDateString);
            if (daysSinceStartDate > CumulativeFlowDiagramSettingsManager.maxAllowedTimePeriodDays) {
                timePeriod.settings = new Date(
                    endDate.getUTCFullYear(),
                    endDate.getUTCMonth(),
                    endDate.getUTCDate() - CumulativeFlowDiagramSettingsManager.maxAllowedTimePeriodDays
                )
                    .toISOString()
                    .substr(0, 10);
            } else if (daysSinceStartDate < CumulativeFlowDiagramSettingsManager.minAllowedTimePeriodDays) {
                timePeriod.settings = new Date(
                    endDate.getUTCFullYear(),
                    endDate.getUTCMonth(),
                    endDate.getUTCDate() - CumulativeFlowDiagramSettingsManager.minAllowedTimePeriodDays
                )
                    .toISOString()
                    .substr(0, 10);
            }
        }

        return parsedSettings;
    }

    private async createDefaultSettings(teamContext: WidgetTeamContext): Promise<{ title: string; settings: CumulativeFlowDiagramSettings }> {
        const project = getProject(this.context);
        const queryService = this.context.getService<ICacheableQueryService>(IDashboardsCacheableQueryServiceName);
        const query = new LowestLevelVisibleBoardQuery(this.command, project.id, teamContext.id);
        const board = await queryService.getCacheableQueryResult(query);

        if (board.BoardId == null) {
            throw new PlaceholderBoardUnsupportedError(project, teamContext, board);
        }

        const defaultSettings: CumulativeFlowDiagramSettings = {
            chartDataSettings: {
                project: project.id,
                team: teamContext.id,
                board: board.BoardId,
                boardLane: null, // Default swimlane is the "All" option which is represented by null
                includeFirstBoardColumn: false,
                timePeriod: {
                    identifier: "RollingPeriod",
                    settings: 30
                }
            },
            themeName: "Blue"
        };

        return {
            title: format(Resources.DefaultTitle_Format, teamContext.name, board.BoardName),
            settings: defaultSettings
        };
    }
}
